import { Box, Card, CircularProgress, Typography } from '@mui/material'
import dayjs from 'dayjs'
import React, { useEffect, useRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroller'
import ImgTag from '../../../components/ImgTag'

interface IFacebookMessage {
  id: string
  from: { id: string }
  message?: string
  sticker?: string
  attachments?: { data: IFacebookAttachments[] }
  attachment?: { payload: { elements: IOrderElement[] } }
  created_time: string
}

interface IFacebookAttachments {
  image_data: { url: string }
  payload?: {
    url: string
  }
}

interface IOrderElement {
  title: string
  subtitle: string
  quantity: number
  price: string
  currency: string
  image_url: string
}

interface ChatMessagesProps {
  messageListData: IFacebookMessage[]
  onHandleMessageList: () => void
  messagePaging: boolean
  isLoadingMessage: boolean
  chat: { pageId: string; name: string }
  setMessageOrderDetail: (orderDetail: Partial<IOrderElement>) => void
  setIsOpenedOrderDetailModal: (isOpen: boolean) => void
  image?: string
  isLoading?: boolean
  firstLoad?: boolean
  handleSetFirstLoad: (firstLoad: boolean) => void
}

const ChatMessages = (props: ChatMessagesProps) => {
  const {
    messageListData,
    onHandleMessageList,
    messagePaging,
    chat,
    setMessageOrderDetail,
    setIsOpenedOrderDetailModal,
    image = '',
    isLoading,
    firstLoad,
    handleSetFirstLoad,
  } = props

  const scrollRef = useRef<HTMLDivElement>(null)
  const [firstMessageId, setFirstMessageId] = useState<string>('')

  useEffect(() => {
    if (scrollRef.current && firstLoad) {
      console.log('scroll to bottom')
      let height = (scrollRef.current.scrollHeight ?? 450) + 1000
      scrollRef.current.scrollTop = height
      setTimeout(() => {
        handleSetFirstLoad(false)
      }, 1000)
    }
  }, [scrollRef, chat, firstLoad])

  const handleScroll = () => {
    if (!isLoading && !firstLoad) {
      onHandleMessageList()
    }
    return
  }

  const renderMessageType = (
    messageObject: IFacebookMessage,
    isPageMessage: boolean,
  ) => {
    const { message, sticker, attachments, attachment } = messageObject
    if (message) {
      return (
        <Box sx={messageBoxStyles(isPageMessage)}>
          <Typography
            variant="subtitle1"
            sx={{ color: isPageMessage ? '#ffffff' : '#000000' }}
          >
            {message}
          </Typography>
        </Box>
      )
    } else if (sticker) {
      return (
        <img
          src={sticker}
          alt="sticker"
          style={{
            maxWidth: '100px',
            maxHeight: '100px',
            objectFit: 'cover',
            borderRadius: '15px',
            marginRight: 2,
            marginTop: 2,
          }}
        />
      )
    } else if (attachments?.data?.length) {
      return attachments.data.map((item, index) => (
        <img
          key={index}
          src={item?.image_data?.url ?? item?.payload?.url ?? ''}
          alt="attachment"
          style={{
            maxWidth: '300px',
            maxHeight: '300px',
            objectFit: 'cover',
            borderRadius: '15px',
            marginRight: 2,
            marginTop: 2,
          }}
        />
      ))
    } else if (attachment) {
      return attachment.payload.elements.map((item, index) => (
        <Card
          key={index}
          sx={{ padding: 2, cursor: 'pointer' }}
          onClick={() => handleOrderDetail(item)}
        >
          <OrderDetail item={item} />
        </Card>
      ))
    }
    return null
  }

  const handleOrderDetail = (item: IOrderElement) => {
    setMessageOrderDetail({
      title: item.title,
      subtitle: item.subtitle,
      quantity: item.quantity,
      price: item.price,
      currency: item.currency,
      image_url: item.image_url,
    })
    setIsOpenedOrderDetailModal(true)
  }

  const messageBoxStyles = (isPageMessage: boolean) => ({
    borderRadius: '15px',
    backgroundColor: isPageMessage ? '#0084FF' : '#F0F0F0',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    maxWidth: '600px',
    paddingY: '3px',
    paddingX: '7px',
  })

  return (
    <Box
      ref={scrollRef}
      sx={{ marginTop: 0, width: '100%', height: 450, overflowY: 'auto', p: 1 }}
    >
      <InfiniteScroll
        pageStart={0}
        loadMore={!firstLoad ? handleScroll : () => {}}
        hasMore={!!messagePaging}
        isReverse={true} // เพื่อเลื่อนขึ้นด้านบน
        useWindow={false}
        loader={
          <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
            กำลังโหลดเพิ่มเติม...
          </Box>
        }
      >
        {messageListData.map((messageObject, index) => {
          const isPageMessage = messageObject.from.id === chat.pageId
          const showTimestamp = shouldShowTimestamp(messageListData, index)
          return (
            <Box key={index} id={messageObject.id}>
              {showTimestamp && (
                <Timestamp createdTime={messageObject.created_time} />
              )}
              <Box sx={messageContainerStyles(isPageMessage)}>
                {renderProfileImage(
                  messageListData,
                  index,
                  chat,
                  isPageMessage,
                  image,
                )}
                {renderMessageType(messageObject, isPageMessage)}
              </Box>
            </Box>
          )
        })}
      </InfiniteScroll>
    </Box>
  )
}

const Loader: React.FC<{ isLoadingMessage: boolean }> = ({
  isLoadingMessage,
}) => (
  <div
    className="loader"
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: '10px',
    }}
  >
    {isLoadingMessage && <CircularProgress />}
  </div>
)

const Timestamp: React.FC<{ createdTime: string }> = ({ createdTime }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', fontSize: '12px' }}>
    <Typography variant="caption" color="text.secondary">
      {dayjs(createdTime).locale('th').format('D MMM YYYY HH:mm')}
    </Typography>
  </Box>
)

const OrderDetail: React.FC<{ item: IOrderElement }> = ({ item }) => (
  <>
    <ImgTag imageUrl={item.image_url} name={item.title} size={40} />
    <Typography variant="subtitle1">{item.title}</Typography>
    <Typography variant="subtitle2">{item.subtitle}</Typography>
    <Typography variant="subtitle2">
      {item.quantity} x {item.price} {item.currency}
    </Typography>
  </>
)

const messageContainerStyles = (isPageMessage: boolean) => ({
  marginTop: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'start',
  justifyContent: isPageMessage ? 'flex-end' : 'flex-start',
})

const shouldShowTimestamp = (
  messageListData: IFacebookMessage[],
  index: number,
): boolean => {
  const currentTime = new Date(messageListData[index]?.created_time).getTime()
  const previousTime = new Date(
    messageListData[index - 1]?.created_time,
  ).getTime()
  return Math.abs(currentTime - previousTime) > 3 * 60 * 1000
}

const renderProfileImage = (
  messageListData: IFacebookMessage[],
  index: number,
  chat: { pageId: string; name: string },
  isPageMessage: boolean,
  image?: string,
) => {
  const isSameSenderAsBefore =
    messageListData[index - 1]?.from.id === messageListData[index].from.id
  if (!isPageMessage && !isSameSenderAsBefore) {
    return (
      <Box sx={{ marginRight: '7px' }}>
        <ImgTag imageUrl={image} name={chat.name} size={40} />
      </Box>
    )
  }
  return <Box sx={{ marginRight: '47px' }} />
}

export default ChatMessages
